import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Button, Checkbox, FormControl, IconButton, MenuItem, OutlinedInput, Select, Stack, Tooltip, Typography } from "@mui/material";

import { api } from "../../../../../utils";
import { IError, ReportLoadingType } from "../../../../../models/ReportPageModels";
import { CustomNoRowsOverlay, ExportToolbar, PaginationModel } from "../../../../../models/ReportGridModels";


const billingsColumns = [
    { field: 'id', headerName: 'uniqueId', width: 1, sortable: false, hide: true, disableExport: true },
    { field: 'company', headerName: 'Client', width: 300, sortable: true, editable: false, hide: false, },
    { field: 'boothId', headerName: 'Booth Id', width: 200, sortable: true, editable: false },
    { field: 'location', headerName: 'Site location', width: 400, sortable: true, editable: false },
    { field: 'desktopVehicles', headerName: 'Desktop vehicles', width: 150, sortable: true, editable: false },
    { field: 'mobileVehicles', headerName: 'Mobile vehicles', width: 150, sortable: true, editable: false },
    { field: 'uniqueVehicles', headerName: 'Unique vehicles', width: 150, sortable: true, editable: false },
]

const columnVisibility = {
    id: false
}

interface CompaniesResponseModel {
    data: CompaniesData;
    error: IError;
}
interface CompaniesData {
    companies: string[];
}

interface MonthlyUsageRequestModel {
    month: Dayjs;
    companies: string[];
}

interface MonthlyUsageReportResponseModel {
    data: MonthlyUsageReportData;
    error: IError;
}

interface MonthlyUsageReportData {
    report: MonthlyUsageReportModel[]
}

interface MonthlyUsageReportModel {
    id: string;
    company: string;
    boothId: string;
    location: string;
    desktopVehicles: number;
    mobileVehicles: number;
    uniqueVehicles: number;
}


function MonthlyUsageReport() {
    const { enqueueSnackbar } = useSnackbar();

    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 100,
        page: 0,
    });

    const [month, setMonth] = useState<Dayjs>(dayjs());
    var monthCopy = useRef<Dayjs>(dayjs());

    const [companies, setCompanies] = useState<string[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

    const [report, setReport] = useState<MonthlyUsageReportModel[]>([]);

    const [billingLoadingType, setBillingLoadingType] = useState<ReportLoadingType>(ReportLoadingType.SelectClient); // loading, error, noreports, selectClient

    const [selectAll, setSelectAll] = useState<boolean>(true);

    useEffect(() => {
        GetCompanies();
    }, [])

    const GetCompanies = async () => {
        try {
            var resp: CompaniesResponseModel = await api.get("api/Deduplication/GetCompanies", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setCompanies(resp.data.companies);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SelectCompany(event: any) {
        var value = event.target.value;
        setSelectedCompanies(value);

        setSelectAll(!companies.every(x => value.includes(x)))
    }

    const LoadMonthlyReport = async () => {
        if (selectedCompanies.length === 0)
            return;

        setBillingLoadingType(ReportLoadingType.Loading)
        setReport([]);

        var reqBody: MonthlyUsageRequestModel = {
            month: monthCopy.current,
            companies: selectedCompanies
        };

        try {
            var resp: MonthlyUsageReportResponseModel = await api.post("api/Admin/MonthlyUsageReport", {
                json: reqBody,
                timeout: 120 * 1000,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setReport(resp.data.report);
                if (resp.data.report.length === 0)
                    setBillingLoadingType(ReportLoadingType.Noreports);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
                setBillingLoadingType(ReportLoadingType.Error)
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
            setBillingLoadingType(ReportLoadingType.Error)
        }
    };

    function SelectAll() {
        if (companies.every(x => selectedCompanies.includes(x))) {
            setSelectedCompanies([]);
            setSelectAll(true);
        }
        else {
            setSelectedCompanies(companies);
            setSelectAll(false);
        }
    }

    function GetDatagridHeight() {
        return window.innerHeight - 130;
    }

    return (
        <Stack direction='row'>
            <Stack
                direction="column"
                sx={{ width: '320px' }}
                alignItems="center"
                justifyContent="flex-start"
                spacing={1.5}>
                <p>Select month</p>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        format="MM/YYYY"
                        views={['year', 'month']}
                        openTo={'month'}
                        value={month}
                        onChange={(param: any) => {
                            setMonth(param);
                            monthCopy.current = dayjs(param);
                        }}
                    />
                </LocalizationProvider>

                <p>Client</p>
                <Stack
                    direction="row"
                    sx={{ width: "260px" }}>
                    <FormControl sx={{ m: 1, width: "200px" }}>
                        <Select
                            multiple
                            value={selectedCompanies}
                            onChange={SelectCompany}
                            size="small"
                            sx={{ height: "40px" }}
                            input={<OutlinedInput label="Tag" />}
                            renderValue={(selected: any) => selected.join(', ')}>
                            {companies.map((item: string, index: number) => (
                                <MenuItem
                                    key={index}
                                    value={item}>
                                    <Checkbox sx={{ padding: "0px" }} checked={selectedCompanies.indexOf(item) > -1} />
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title={
                        <Typography
                            fontSize={"16px"}>
                            {selectAll ?
                                "Select all"
                                :
                                "Deselect all"
                            }

                        </Typography>}>
                        <IconButton
                            color="primary"
                            size="large"
                            sx={{ width: "30px", height: "30px", marginTop: "10px" }}
                            onClick={SelectAll}>
                            {selectAll ?
                                <CheckBoxIcon />
                                :
                                <CheckBoxOutlineBlankIcon />
                            }
                        </IconButton>
                    </Tooltip>

                </Stack>


                <Button
                    variant="outlined"
                    onClick={LoadMonthlyReport}
                    sx={{ left: '17px' }}>
                    Monthly usage report
                </Button>
            </Stack>
            <div style={{ width: '100%', height: GetDatagridHeight() }}>
                <DataGrid
                    rows={report ?? []}
                    columns={billingsColumns}
                    columnVisibilityModel={columnVisibility}
                    pagination
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    keepNonExistentRowsSelected={true}
                    disableColumnMenu={false}
                    slots={{
                        toolbar: ExportToolbar,
                        noRowsOverlay: CustomNoRowsOverlay
                    }}
                    slotProps={{
                        noRowsOverlay: {
                            reportLoadingType: billingLoadingType
                        }
                    }}
                />
            </div>
        </Stack>
    )
}

export default MonthlyUsageReport;